import type { Color } from '../../engine';

/**
 * The required glyph for each color (§3.4), drawn in pixels on a 9 × 9 grid: ✚ Red, ✖ Blue,
 * knight "L" Green, ● Yellow. `#` is the glyph's color, `o` the same a shade darker (its lower
 * edges, and the Yellow's ring).
 */
const GRIDS: Readonly<Record<Color, readonly string[]>> = {
  R: [
    '...###...',
    '...###...',
    '...###...',
    '#########',
    '#########',
    'ooo###ooo',
    '...###...',
    '...###...',
    '...ooo...',
  ],
  B: [
    '##.....##',
    '###...###',
    '.###.###.',
    '..#####..',
    '...###...',
    '..#####..',
    '.###.###.',
    '###o.o###',
    'oo.....oo',
  ],
  G: [
    '###......',
    '###......',
    '###......',
    '###......',
    '###......',
    '###......',
    '#########',
    '#########',
    'ooooooooo',
  ],
  Y: [
    '..#####..',
    '.#######.',
    '###ooo###',
    '##o###o##',
    '##o###o##',
    '##o###o##',
    '###ooo###',
    '.#######.',
    '..ooooo..',
  ],
};

/** The cells of a grid holding `mark`, as path data, a rectangle per run along each row. */
function cells(grid: readonly string[], mark: string): string {
  let d = '';
  grid.forEach((row, y) => {
    for (let x = 0; x < row.length;) {
      if (row[x] !== mark) {
        x++;
        continue;
      }
      let run = 1;
      while (row[x + run] === mark) run++;
      d += `M${x} ${y}h${run}v1h${-run}z`;
      x += run;
    }
  });
  return d;
}

export function Glyph({
  color,
  size = 18,
  title,
}: {
  color: Color;
  size?: number;
  title?: string;
}) {
  const grid = GRIDS[color];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 9"
      shapeRendering="crispEdges"
      className="glyph"
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <path d={cells(grid, '#')} fill="currentColor" />
      <path d={cells(grid, 'o')} fill="currentColor" />
      <path d={cells(grid, 'o')} fill="rgba(10, 6, 30, 0.35)" />
    </svg>
  );
}

/** The four color marks in a row: the Wild's emblem. */
export function WildEmblem({ size = 18 }: { size?: number }) {
  return (
    <span className="wild-emblem" aria-hidden="true">
      {(['R', 'B', 'G', 'Y'] as const).map((c) => (
        <span key={c} className={`c-${c}`}>
          <Glyph color={c} size={size / 1.6} />
        </span>
      ))}
    </span>
  );
}
